import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdVisibility,
  MdCheck,
  MdClose,
  MdFlight,
  MdSettings,
  MdInfo,
  MdBusiness,
  MdPerson,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

const ConfirmStatusText: any = { 0: "Pending", 1: "Approved", 2: "Rejected", 3: "Expired" };
const ConfirmStatusColor: any = { 0: "#F59E0B", 1: "#16A34A", 2: "#DC2626", 3: "#6B7280" };

const BoolBadge = ({ value }: any) => (
  <Badge
    label={value ? "Yes" : "No"}
    color={value ? "#16A34A" : "#DC2626"}
    bg={value ? "rgba(22,163,74,.15)" : "rgba(220,38,38,.15)"}
  />
);

const InfoRow = ({ icon: Icon, label, value }: any) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "28px 220px 1fr",
      gap: 10,
      padding: "6px 0",
      borderBottom: "1px solid #E5E7EB",
      fontSize: 12,
    }}
  >
    <Icon size={22} color="#25b3ebff" />
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value ?? "-"}</span>
  </div>
);

const Card = ({ icon: Icon, title, children }: any) => (
  <div
    style={{
      border: "1px solid #E5E7EB",
      borderRadius: 18,
      padding: 16,
      background: "#FFFFFFAA",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        background:
          "linear-gradient(135deg,#3B82F6 0%,#60A5FA 50%,#93C5FD 100%)",
        color: "#fff",
        borderRadius: 8,
        padding: 6,
      }}
    >
      <Icon size={22} />
      <strong>{title}</strong>
    </div>
    {children}
  </div>
);

const ConfirmStatusModal = ({ row, onClose, onSaved }: any) => {
  const api = useMemo(() => createApi(), []);
  const [status, setStatus] = useState(row.confirmStatus);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await api.businessJetWanted.confirmStatus(row.id, status);
    setSaving(false);
    onClose();
    onSaved();
  };

  return (
    <Modal open onClose={onClose}>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Confirm Status</div>
      <select
        value={status}
        onChange={(e) => setStatus(+e.target.value)}
        style={{ width: "100%", height: 36, marginBottom: 16 }}
      >
        <option value={0}>Pending</option>
        <option value={1}>Approved</option>
        <option value={2}>Rejected</option>
      </select>
      <div style={{ display: "flex", gap: 8 }}>
        <Button icon={MdCheck} loading={saving} onClick={save}>
          Confirm
        </Button>
        <Button icon={MdClose} variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

const BusinessJetWantedDetailModal = ({ id, onClose }: any) => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    api.businessJetWanted.getById(id).then((r: any) => {
      setData(r?.data?.data ?? r?.data);
    });
  }, [id]);

  if (!data) return null;

  return (
    <Modal open fullScreen onClose={onClose}>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14, padding: "24px 24px 0 24px" }}>Business Jet Wanted Detail</div>
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 22, marginBottom: 24 }}>
          Business Jet Sale / Lease / Rental Wanted
        </h1>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(420px,1fr))",
            gap: 20,
          }}
        >
          <Card icon={MdInfo} title="General">
            <InfoRow icon={MdInfo} label="Headline" value={data.headline} />
            <InfoRow
              icon={MdInfo}
              label="Description"
              value={data.description}
            />
            <InfoRow
              icon={MdInfo}
              label="Confirm Status"
              value={
                <Badge
                  label={ConfirmStatusText[data.confirmStatus]}
                  color={ConfirmStatusColor[data.confirmStatus]}
                />
              }
            />
            <InfoRow icon={MdInfo} label="Trade Type" value={data.tradeType} />
          </Card>

          <Card icon={MdFlight} title="Aircraft Criteria">
            <InfoRow
              icon={MdFlight}
              label="Aircraft Type"
              value={data.anyAircraftType ? "Any" : data.aircraftType?.name}
            />
            <InfoRow icon={MdSettings} label="YOM" value={data.yom} />
            <InfoRow icon={MdSettings} label="Capacity" value={data.capacity} />
            <InfoRow
              icon={MdSettings}
              label="Condition"
              value={data.conditionWanted}
            />
            <InfoRow
              icon={MdSettings}
              label="Flight Range"
              value={data.flightRange}
            />
            <InfoRow icon={MdSettings} label="MTOW" value={data.mtow} />
          </Card>

          <Card icon={MdSettings} title="Engine Criteria">
            <InfoRow
              icon={MdSettings}
              label="Engine Type"
              value={data.anyEngineType ? "Any" : data.engineType?.code}
            />
            <InfoRow
              icon={MdSettings}
              label="Engine Model Detail"
              value={data.engineModelDetail}
            />
          </Card>

          <Card icon={MdInfo} title="Commercial">
            <InfoRow icon={MdInfo} label="Buyer Name" value={data.buyerName} />
            <InfoRow
              icon={MdInfo}
              label="Lessee Name"
              value={data.lesseeName}
            />
            <InfoRow
              icon={MdInfo}
              label="Availability Date"
              value={
                data.availabilityDateWanted
                  ? new Date(data.availabilityDateWanted).toLocaleDateString()
                  : "-"
              }
            />
            <InfoRow
              icon={MdInfo}
              label="Delivery Terms"
              value={data.deliveryTermsWanted}
            />
            <InfoRow
              icon={MdInfo}
              label="Redelivery Terms"
              value={data.redeliveryTermsWanted}
            />
            <InfoRow
              icon={MdInfo}
              label="Lease Start Date"
              value={
                data.leaseStartDateWanted
                  ? new Date(data.leaseStartDateWanted).toLocaleDateString()
                  : "-"
              }
            />
            <InfoRow
              icon={MdInfo}
              label="Lease Duration (Month)"
              value={data.leaseDurationMonthWanted}
            />
            <InfoRow icon={MdInfo} label="Operator" value={data.operatorName} />
            <InfoRow
              icon={MdInfo}
              label="Rental Period"
              value={
                data.rentalStartDate && data.rentalEndDate
                  ? `${new Date(
                      data.rentalStartDate
                    ).toLocaleDateString()} - ${new Date(
                      data.rentalEndDate
                    ).toLocaleDateString()}`
                  : "-"
              }
            />
          </Card>

          <Card icon={MdInfo} title="Location">
            <InfoRow
              icon={MdInfo}
              label="Continent"
              value={data.location?.continent?.name}
            />
            <InfoRow
              icon={MdInfo}
              label="Country"
              value={data.location?.country?.name}
            />
            <InfoRow
              icon={MdInfo}
              label="City"
              value={data.location?.city?.name}
            />
            <InfoRow
              icon={MdFlight}
              label="Airport"
              value={
                data.airport
                  ? `${data.airport.name} (${data.airport.codeIATA})`
                  : "-"
              }
            />
          </Card>

          <Card icon={MdPerson} title="User">
            <InfoRow
              icon={MdPerson}
              label="Username"
              value={data.user?.username}
            />
            <InfoRow icon={MdInfo} label="Email" value={data.user?.email} />
          </Card>

          <Card icon={MdBusiness} title="Company">
            <InfoRow
              icon={MdBusiness}
              label="Company"
              value={data.company?.companyName}
            />
          </Card>
        </div>
      </div>
    </Modal>
  );
};

export const BusinessJetWantedForms = () => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [statusRow, setStatusRow] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r: any = await api.businessJetWanted.getAll();
    setData(r?.data?.data ?? r?.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        key: "aircraft",
        title: "Aircraft",
        accessor: (r: any) =>
          r.anyAircraftType ? "Any" : r.aircraftType?.name,
      },
      {
        key: "type",
        title: "Trade Type",
        accessor: (r: any) =>
          r.tradeType === 1 ? "Sale" : r.tradeType === 2 ? "Lease" : "Rental",
      },
      {
        key: "engine",
        title: "Engine",
        accessor: (r: any) => (r.anyEngineType ? "Any" : r.engineType?.code),
      },
      {
        key: "company",
        title: "Company",
        accessor: (r: any) => r.company?.companyName,
      },
      {
        key: "status",
        title: "Status",
        accessor: (r: any) => r.confirmStatus,
        render: (_: any, v: any) => (
          <Badge
            label={ConfirmStatusText[v]}
            color={ConfirmStatusColor[v]}
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        render: (r: any) => (
          <GroupButton
            items={[
              {
                key: "detail",
                icon: MdVisibility,
                color: "#2563EB",
                onClick: () => setDetailId(r.id),
              },
              {
                key: "confirm",
                icon: MdCheck,
                color: "#F59E0B",
                onClick: () => setStatusRow(r),
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>Business Jet Wanted</div>
        <div style={{ fontSize: 13, color: "#64748B" }}>
          Manage sale / lease / rental wanted business jets
        </div>
      </div>

      <Table loading={loading} columns={columns} data={data} />

      {detailId && (
        <BusinessJetWantedDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
        />
      )}

      {statusRow && (
        <ConfirmStatusModal
          row={statusRow}
          onClose={() => setStatusRow(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};
